import { IAddSocialMediaBulkResponseRow } from "@shared/lib/types/influencer";
import { Modal } from "@shared/ui";
import { FileSpreadsheet, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { SheetModeIntro } from "./SheetModeIntro";
import { SingleCreatorForm } from "./SingleCreatorForm";
import { SingleCreatorReview } from "./SingleCreatorReview";
import styles from "./styles.module.scss";

interface AddCreatorModalSingleProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: { platform: string; username: string }) => void;
  onOpenBulk: () => void;
  isAdding: boolean;
  isAdded: boolean;
  successData: IAddSocialMediaBulkResponseRow[];
  handleSuggest: () => void;
  isSuggesting: boolean;
}

export function AddCreatorModalSingle({
  isOpen,
  onClose,
  onAdd,
  onOpenBulk,
  isAdding,
  isAdded,
  successData,
  handleSuggest,
  isSuggesting,
}: AddCreatorModalSingleProps) {
  const [mode, setMode] = useState<"sheet" | "single">("sheet");
  const [platform, setPlatform] = useState<"instagram" | "youtube" | "tiktok">(
    "instagram"
  );
  const [handle, setHandle] = useState("");
  const [status, setStatus] = useState<"idle" | "validating" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setMode("sheet");
        setPlatform("instagram");
        setHandle("");
        setStatus("idle");
        setErrorMsg("");
      }, 300);
    }
  }, [isOpen]);

  const validateHandle = () => {
    if (!handle.trim()) {
      setErrorMsg("Please enter a username");
      setStatus("error");
      return;
    }
    setStatus("validating");
    setErrorMsg("");
    onAdd({ platform, username: handle.replace("@", "") });
  };

  const isReviewMode = isAdded && successData.length > 0;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={isReviewMode ? "Review & Import" : "Add Creator"}
      footer={null}
      width={480}
      destroyOnClose
      className={styles.modal}
      styles={{
        body: { padding: 0 },
        header: { marginBottom: 0, padding: 20 },
        content: { padding: 0 },
      }}
    >
      <div className={styles.modalContent}>
        {isReviewMode ? (
          <SingleCreatorReview
            handle={handle}
            successData={successData}
            isSuggesting={isSuggesting}
            onClose={onClose}
            onImport={handleSuggest}
          />
        ) : (
          <>
            <div className={styles.modeSwitchWrap}>
              <div className={styles.modeSwitch}>
                <button
                  type="button"
                  onClick={() => setMode("sheet")}
                  className={`${styles.modeButton} ${
                    mode === "sheet" ? styles.modeButtonActive : ""
                  }`}
                >
                  <FileSpreadsheet size={13} /> Upload sheet
                </button>
                <button
                  type="button"
                  onClick={() => setMode("single")}
                  className={`${styles.modeButton} ${
                    mode === "single" ? styles.modeButtonActive : ""
                  }`}
                >
                  <UserPlus size={13} /> Single creator
                </button>
              </div>
            </div>
            <div className={styles.modalBody}>
              {mode === "sheet" ? (
                <SheetModeIntro
                  onContinue={() => {
                    onClose();
                    setTimeout(onOpenBulk, 200);
                  }}
                />
              ) : (
                <SingleCreatorForm
                  platform={platform}
                  handle={handle}
                  status={status}
                  errorMsg={errorMsg}
                  isAdding={isAdding}
                  onPlatformChange={setPlatform}
                  onHandleChange={(value) => {
                    setHandle(value);
                    if (status === "error") setStatus("idle");
                  }}
                  onSubmit={validateHandle}
                />
              )}
            </div>
            {mode === "sheet" && (
              <div className={styles.modalFooterLink}>
                <button
                  type="button"
                  onClick={() => setMode("single")}
                  className={styles.bulkLink}
                >
                  <UserPlus size={14} /> Prefer to add one creator?
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
